import type { useTranslate } from "@refinedev/core";
import type {
  PlaybookCreationRequest,
  PlaybookUpdateRequest,
} from "pages/media/types";
import type {
  AddonObject,
  AddonSettings,
} from "../context/PlaybookDraftContext";
import type { AddonId } from "../data/addons";
import {
  SETTINGS_BRANCHES,
  isAddOnType,
  mapDraftToEnabledAddOns,
  nonEmptyBranch,
  playbookSettingsKey,
} from "../data/addonApi";

export const formatLanguageName = (
  value: string,
  t: ReturnType<typeof useTranslate>
) => {
  const [lang, country] = value.split("-");
  const languageName = t(`nativeLanguageNames.${lang}`);
  const countryName = t(`nativeCountryNames.${country}`);
  return countryName ? `${languageName} (${countryName})` : languageName;
};

/**
 * `PlaybookInput` is a text input (antd stores `e.target.value` STRINGS even
 * with type="number"), but the API-shaped draft needs real numbers. Pass as
 * `normalize` on a numeric PlaybookFormItem so the stored value is a number;
 * empty/invalid input stores undefined (field falls back to defaults / is
 * omitted from payloads).
 */
export const normalizeNumericInput = (value: unknown): number | undefined => {
  if (value === "" || value == null) return undefined;
  const numeric = Number(value);
  return Number.isNaN(numeric) ? undefined : numeric;
};

/**
 * Fraction-stored, percent-displayed field (e.g. quiz `passing_score`: the API
 * stores 0.7, the input shows 70). Spread onto a PlaybookFormItem.
 */
export const percentFieldProps = {
  getValueProps: (value: unknown) => ({
    value: typeof value === "number" ? Math.round(value * 100) : value,
  }),
  normalize: (value: unknown) => {
    const numeric = normalizeNumericInput(value);
    return numeric === undefined ? undefined : numeric / 100;
  },
};

export interface PlaybookIdentity {
  name: string;
  description?: string;
}

/**
 * Inverse of `mapPlaybookToDraft`: the draft's configuration as playbook fields
 * — `enabled_add_ons`, the General branches, and each add-on's non-empty
 * `<add_on>_generation_settings` / `<add_on>_functional_settings` branch.
 *
 * Only the backend add-ons make it in. The UI-only prototypes have no playbook
 * fields at all, so a saved playbook cannot carry them — the same limitation
 * the PATCH cascade has, which is why the cascade still runs alongside a
 * `playbook_id`.
 *
 * Untyped on purpose: the settings key is computed, so TS can't narrow it to
 * the one field whose settings union the value matches. The two exported
 * wrappers below do the cast once.
 */
const mapDraftToPlaybookSettings = (
  draft: AddonObject,
  /** Experience-wide General settings (language, side panel, brand kit,
   *  avatar …) — kept outside `draft` since "general" is not a rail add-on. */
  general?: AddonSettings
): Record<string, unknown> => {
  const payload: Record<string, unknown> = {
    enabled_add_ons: { enabled_add_ons: mapDraftToEnabledAddOns(draft) },
  };

  if (general) {
    for (const branch of SETTINGS_BRANCHES) {
      const value = general[branch];
      if (!nonEmptyBranch(value)) continue;
      const kind =
        branch === "generation_settings" ? "generation" : "functional";
      payload[playbookSettingsKey("General", kind)] = value;
    }
  }

  for (const id of Object.keys(draft) as AddonId[]) {
    if (!isAddOnType(id)) continue;
    const stored = draft[id] ?? {};
    for (const branch of SETTINGS_BRANCHES) {
      const value = stored[branch];
      if (!nonEmptyBranch(value)) continue;
      const kind =
        branch === "generation_settings" ? "generation" : "functional";
      payload[playbookSettingsKey(id, kind)] = value;
    }
  }
  return payload;
};

/** The draft as a new playbook, for `POST media/{organizationId}/playbooks`. */
export const mapDraftToPlaybookPayload = (
  draft: AddonObject,
  { name, description }: PlaybookIdentity,
  general?: AddonSettings
): PlaybookCreationRequest =>
  ({
    name,
    ...(description && { description }),
    ...mapDraftToPlaybookSettings(draft, general),
  }) as PlaybookCreationRequest;

/**
 * The draft as an edit of an existing playbook, for
 * `PATCH media/{organizationId}/playbooks/{id}` — the same configuration
 * without the identity, which playbook-edit mode never touches (the name is
 * changed from the carousel's Rename, not from Customize).
 *
 * Note this is a full replacement of the configuration, not a partial one: an
 * add-on the user removed from the draft is absent from `enabled_add_ons`, and
 * PATCHing that drops it from the playbook.
 */
export const mapDraftToPlaybookUpdate = (
  draft: AddonObject,
  general?: AddonSettings
): PlaybookUpdateRequest =>
  mapDraftToPlaybookSettings(draft, general) as PlaybookUpdateRequest;
